import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HomeService } from '../home-service/home.service';

interface IconeTarefa {
  nome: string;
  fontSet?: string;
}

interface ContadoresTarefa {
  paraHoje: number;
  atrasadas: number;
  pendentes: number;
  concluidas: number;
}

type OrigemTarefa = 'obra' | 'empresa';
type FiltroTarefa = 'todas' | 'obra' | 'empresa';

interface TarefaRotina {
  taskId: number;
  origem: OrigemTarefa;
  titulo: string;
  actionDescription: string;
  followUpDate: string;
  followUpLabel: string;
  isAtrasada: boolean;
}

const CONTADORES_ZERADOS: ContadoresTarefa = {
  paraHoje: 0,
  atrasadas: 0,
  pendentes: 0,
  concluidas: 0,
};

@Component({
  selector: 'app-bloco-tarefas',
  standalone: true,
  imports: [CommonModule, MatIconModule, RouterModule],
  templateUrl: './bloco-tarefas.component.html',
  styleUrls: ['./bloco-tarefas.component.scss'],
})
export class BlocoTarefasComponent implements OnInit {
  loading = true;
  filtro: FiltroTarefa = 'todas';

  private contadoresObra: ContadoresTarefa = { ...CONTADORES_ZERADOS };
  private contadoresEmpresa: ContadoresTarefa = { ...CONTADORES_ZERADOS };
  private todasTarefas: TarefaRotina[] = [];

  private readonly QTD_EXIBIDA = 7;

  private readonly iconesPorAcao: Record<string, IconeTarefa> = {
    'Telefonar':            { nome: 'call' },
    'Ligação':              { nome: 'call' },
    'Visitar':              { nome: 'directions_car' },
    'Visita':               { nome: 'directions_car' },
    'Em especificação':     { nome: 'description' },
    'Em negociação':        { nome: 'handshake', fontSet: 'material-symbols-outlined' },
    'Em orçamento':         { nome: 'request_quote', fontSet: 'material-symbols-outlined' },
    'Enviar orçamento':     { nome: 'send' },
    'Futura prospecção':    { nome: 'explore' },
    'Previsão de cotação':  { nome: 'event' },
    'Mensagem WhatsApp':    { nome: 'chat' },
  };

  private readonly iconePadrao: IconeTarefa = { nome: 'task_alt' };

  constructor(
    private readonly homeService: HomeService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  get contadores(): ContadoresTarefa {
    if (this.filtro === 'obra') return this.contadoresObra;
    if (this.filtro === 'empresa') return this.contadoresEmpresa;

    return {
      paraHoje:   this.contadoresObra.paraHoje   + this.contadoresEmpresa.paraHoje,
      atrasadas:  this.contadoresObra.atrasadas  + this.contadoresEmpresa.atrasadas,
      pendentes:  this.contadoresObra.pendentes  + this.contadoresEmpresa.pendentes,
      concluidas: this.contadoresObra.concluidas + this.contadoresEmpresa.concluidas,
    };
  }

  get proximasTarefas(): TarefaRotina[] {
    const lista =
      this.filtro === 'todas'
        ? this.todasTarefas
        : this.todasTarefas.filter((t) => t.origem === this.filtro);

    return lista.slice(0, this.QTD_EXIBIDA);
  }

  get exibirFiltro(): boolean {
    return this.contadoresEmpresa.pendentes > 0 || this.contadoresEmpresa.concluidas > 0;
  }

  get rotaVerTodas(): string {
    return this.filtro === 'empresa' ? '/empresas/tarefas' : '/tarefas';
  }

  selecionarFiltro(filtro: FiltroTarefa): void {
    this.filtro = filtro;
  }

  private carregar(): void {
    this.loading = true;

    forkJoin({
      obras: this.homeService.getTaskSummary(30).pipe(catchError(() => of(null))),
      empresas: this.homeService.getCompanyTaskSummary(30).pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ obras, empresas }) => {
        const dObras = obras?.data;
        const dEmpresas = empresas?.data;

        this.contadoresObra = this.mapContadores(dObras);
        this.contadoresEmpresa = this.mapContadores(dEmpresas);

        const tarefasObra: TarefaRotina[] = (dObras?.proximasTarefas ?? []).map((t: any) => ({
          taskId: t.taskID,
          origem: 'obra' as OrigemTarefa,
          titulo: t.workName,
          actionDescription: t.actionDescription,
          followUpDate: t.followUpDate,
          followUpLabel: t.followUpLabel,
          isAtrasada: t.isAtrasada,
        }));

        const tarefasEmpresa: TarefaRotina[] = (dEmpresas?.proximasTarefas ?? []).map((t: any) => ({
          taskId: t.taskId,
          origem: 'empresa' as OrigemTarefa,
          titulo: t.companyName,
          actionDescription: t.actionDescription,
          followUpDate: t.followUpDate,
          followUpLabel: t.followUpLabel,
          isAtrasada: t.isAtrasada,
        }));

        this.todasTarefas = this.ordenar([...tarefasObra, ...tarefasEmpresa]);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private mapContadores(data: any): ContadoresTarefa {
    return {
      paraHoje: data?.paraHoje ?? 0,
      atrasadas: data?.atrasadas ?? 0,
      pendentes: data?.pendentes ?? 0,
      concluidas: data?.concluidas ?? 0,
    };
  }

  private ordenar(tarefas: TarefaRotina[]): TarefaRotina[] {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const peso = (t: TarefaRotina): number => {
      const data = new Date(t.followUpDate);
      data.setHours(0, 0, 0, 0);

      if (data.getTime() === hoje.getTime()) return 0;
      if (data.getTime() < hoje.getTime()) return 1;
      return 2;
    };

    return [...tarefas].sort((a, b) => {
      const diff = peso(a) - peso(b);
      if (diff !== 0) return diff;

      return new Date(a.followUpDate).getTime() - new Date(b.followUpDate).getTime();
    });
  }

  getIconeTarefa(actionDescription: string): IconeTarefa {
    return this.iconesPorAcao[actionDescription] ?? this.iconePadrao;
  }
}